import { TypeInitProps, TypeRequest } from '../../types';
import { encryptParams } from '../../utils/index';
import { WEB_URL } from '../../utils/urls';
export default class PasswordAPI {
  private request: any;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request } = props;
    this.request = request;
  }

  /**
   * @param {boolean} verify_mobile_seperate - whether to verify the mobile captcha seperately
   * @return {Promise} send result
   */
  sendCode = (
    params: {
      verify_mobile_seperate?: boolean;
      extra_params?: object;
    } = {}
  ) => {
    const { verify_mobile_seperate = false, extra_params = {} } = params;
    let type = verify_mobile_seperate ? 22 : 13;
    let data = encryptParams({ type, ...extra_params }, ['type']);
    return this.request({
      url: WEB_URL.SEND_MOBILE_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} code - the captcha
   * @return {Promise} verify result
   */
  validateMobile = (params: { code: string; extra_params?: object }) => {
    const { code, extra_params = {} } = params;
    let data = encryptParams({ code, type: 22, ...extra_params }, [
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.VERIFY_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - new password
   * @param {string} code - the captcha
   * @param {string} ticket - the ticket returned after mobile verification
   * @return {Promise} change result
   */
  changePwdByMobile = (params: {
    password: string;
    code?: string;
    ticket?: string;
    extra_params?: object;
  }) => {
    const { password, code, ticket, extra_params = {} } = params;
    let data = encryptParams({ password, code, ticket, ...extra_params }, [
      'password',
      'code',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.PWD_CHANGE_BY_MOBILE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - new password
   * @param {string} current_password - old password
   * @return {Promise} update result
   */
  updatePwd = (params: {
    password: string;
    current_password: string;
    extra_params?: object;
  }) => {
    const { password, current_password, extra_params = {} } = params;
    let data = encryptParams({ password, current_password, ...extra_params }, [
      'password',
      'current_password',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.PWD_UPDATE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @return {Promise} send result
   */
  sendEmailCode = (params: { extra_params?: object } = {}) => {
    const { extra_params = {} } = params;
    let data = encryptParams({ type: 11, ...extra_params }, ['type']);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} code - the captcha
   * @return {Promise} verify result
   */
  validateEmail = (params: { code: string; extra_params?: object }) => {
    const { code, extra_params = {} } = params;
    let data = encryptParams({ code, type: 11, ...extra_params }, [
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.VERIFY_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password - new password
   * @param {string} code - the captcha
   * @param {string} ticket - the ticket returned after email verification
   * @return {Promise} change result
   */
  changePwdByEmail = (params: {
    password: string;
    code?: string;
    ticket?: string;
    extra_params?: object;
  }) => {
    const { password, code, ticket, extra_params = {} } = params;
    let data = encryptParams({ password, code, ticket, ...extra_params }, [
      'password',
      'code',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.PWD_CHANGE_BY_EMAIL,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} password
   * @return {Promise} set result
   */
  setPassword = (params: { password: string; extra_params?: object }) => {
    const { password, extra_params = {} } = params;
    let data = encryptParams({ password, ...extra_params }, ['password']);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.PWD_SET,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @return {Promise} check result
   */
  checkHasSetPwd = (params: { extra_params?: object } = {}) => {
    const { extra_params = {} } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.PWD_HAS_SET,
        method: 'POST',
        data: extra_params,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @return {Promise} check result
   */
  checkWebHasSetPwd = (params: { extra_params?: object } = {}) => {
    const { extra_params = {} } = params;
    return this.request({
      url: WEB_URL.PWD_HAS_SET_V2,
      method: 'GET',
      data: extra_params,
    });
  };

  /**
   * @param {string} password
   * @param {string} email
   * @param {string} code
   * @return {Promise} check result
   */
  resetPwdLoginByEmail = (params: {
    password: string;
    email: string;
    code: string;
    extra_params?: object;
  }) => {
    const { password, email, code, extra_params = {} } = params;
    let data = encryptParams({ password, email, code, ...extra_params }, [
      'password',
      'email',
      'code',
    ]);
    return this.request({
      url: WEB_URL.PWD_RESET_LOGIN_BY_EMAIL,
      method: 'GET',
      data,
    });
  };

  /*
   * @param {string} password - new password
   * @param {string} code - the email verification code
   * @param {string} email - the verified email
   * @return {Promise} change result
   */
  changePwdByEmailAndLogin = (params: {
    password: string;
    code: string;
    email: string;
    extra_params?: object;
  }) => {
    const { password, code, email, extra_params = {} } = params;
    let data = encryptParams({ password, code, email, ...extra_params }, [
      'password',
      'code',
    ]);
    return this.request({
      url: WEB_URL.PWD_SET_BY_EAMIL_AND_LOGIN,
      method: 'POST',
      data,
    });
  };
}
